/**
 * AuthProvider — React context that wraps the app and exposes authentication
 * state and actions. Uses in-memory token storage via auth-client module.
 *
 * Exposes: isAuthenticated, login, logout, getAccessToken, refreshToken, isLoading
 *
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5, 9.6
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  AuthConfig,
  AuthTokens,
  buildLoginUrl,
  clearTokens,
  exchangeCodeForTokens,
  getTokens,
  initializeAuth,
  isTokenExpired,
  refreshAccessToken,
  revokeAndLogout,
  setTokens,
} from '../services/auth-client';
import { authConfig } from '../config';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface AuthContextValue {
  /** Whether the user has a valid (non-expired) access token. */
  readonly isAuthenticated: boolean;
  /** Whether auth initialization is in progress. */
  readonly isLoading: boolean;
  /** Redirects the user to the Cognito Hosted UI for login. */
  login: () => void;
  /** Revokes the refresh token and redirects to the Cognito logout URL. */
  logout: () => Promise<void>;
  /**
   * Returns the current access token if valid, or attempts a refresh.
   * Returns null if no valid token is available.
   */
  getAccessToken: () => Promise<string | null>;
  /**
   * Explicitly refreshes the access token using the stored refresh token.
   * Returns the new tokens or null if refresh failed.
   */
  refreshToken: () => Promise<AuthTokens | null>;
  /**
   * Exchanges an authorization code for tokens and sets authenticated state.
   * Used by the auth callback page.
   */
  handleCallback: (code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  readonly children: React.ReactNode;
  /** Override config for testing purposes. Uses appConfig.authConfig by default. */
  readonly config?: AuthConfig;
}

export function AuthProvider({ children, config }: AuthProviderProps): React.ReactElement {
  const cfg = config ?? authConfig;
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // On mount, check for existing valid tokens or attempt refresh
  useEffect(() => {
    let cancelled = false;

    async function init(): Promise<void> {
      const state = await initializeAuth(cfg);
      if (!cancelled) {
        setAuthenticated(state.isAuthenticated);
        setLoading(false);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [cfg]);

  const login = useCallback((): void => {
    window.location.href = buildLoginUrl(cfg);
  }, [cfg]);

  const logout = useCallback(async (): Promise<void> => {
    await revokeAndLogout(cfg);
    setAuthenticated(false);
  }, [cfg]);

  const getAccessTokenFn = useCallback(async (): Promise<string | null> => {
    const tokens = getTokens();
    if (!tokens) return null;

    if (!isTokenExpired(tokens)) {
      return tokens.accessToken;
    }

    // Attempt refresh
    const refreshed = await refreshAccessToken(cfg);
    if (refreshed) {
      setAuthenticated(true);
      return refreshed.accessToken;
    }

    // Refresh failed
    clearTokens();
    setAuthenticated(false);
    return null;
  }, [cfg]);

  const refreshTokenFn = useCallback(async (): Promise<AuthTokens | null> => {
    const refreshed = await refreshAccessToken(cfg);
    if (refreshed) {
      setAuthenticated(true);
    } else {
      clearTokens();
      setAuthenticated(false);
    }
    return refreshed;
  }, [cfg]);

  const handleCallback = useCallback(async (code: string): Promise<void> => {
    const tokens = await exchangeCodeForTokens(code, cfg);
    setTokens(tokens);
    setAuthenticated(true);
  }, [cfg]);

  const value: AuthContextValue = {
    isAuthenticated: authenticated,
    isLoading: loading,
    login,
    logout,
    getAccessToken: getAccessTokenFn,
    refreshToken: refreshTokenFn,
    handleCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the auth context. Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
